import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

const IncomingCallHandler = () => {
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const websocketRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    initializeWebSocket();
    
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const initializeWebSocket = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const user = response.data;
      setCurrentUser(user);
      
      const wsUrl = `${WS_URL}/ws/call/${user.id}`;
      console.log('IncomingCallHandler: Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      
      ws.onopen = () => {
        console.log('✅ IncomingCallHandler: WebSocket connected');
      };
      
      ws.onmessage = async (event) => {
        console.log('📨 IncomingCallHandler: Received message:', event.data);
        const message = JSON.parse(event.data);
        
        if (message.type === 'call-request') {
          console.log('📞 Incoming call from:', message.from_user_id);
          // Get caller info
          const matchesResponse = await axios.get(`${API}/matches`, { withCredentials: true });
          const match = matchesResponse.data.find(m => m.user.id === message.from_user_id);
          
          if (match) {
            setIncomingCall({
              from_user_id: message.from_user_id,
              from_user: match.user,
              match_id: message.match_id,
              call_type: message.call_type
            });
            
            // Play ringtone
            if (audioRef.current) {
              audioRef.current.play().catch(e => console.log('Audio play failed:', e));
            }
          } else {
            console.error('No match found for caller:', message.from_user_id);
          }
        } else if (message.type === 'call-end') {
          console.log('Call ended by caller');
          setIncomingCall(null);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ IncomingCallHandler WebSocket error:', error);
      };
      
      ws.onclose = (event) => {
        console.log('IncomingCallHandler WebSocket closed:', event.code);
      };
      
    } catch (error) {
      console.error('WebSocket initialization error:', error);
    }
  };

  const acceptCall = () => {
    if (incomingCall && websocketRef.current) {
      websocketRef.current.send(JSON.stringify({
        type: 'call-accept',
        target_user_id: incomingCall.from_user_id
      }));
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Navigate to call screen
      navigate(`/call/${incomingCall.match_id}/${incomingCall.call_type}`);
      setIncomingCall(null);
    }
  };

  const rejectCall = () => {
    if (incomingCall && websocketRef.current) {
      websocketRef.current.send(JSON.stringify({
        type: 'call-reject',
        target_user_id: incomingCall.from_user_id
      }));
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      setIncomingCall(null);
    }
  };

  if (!incomingCall) return null;

  return (
    <>
      {/* Ringtone audio */}
      <audio ref={audioRef} loop>
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OWUQ0NVrLn7K1bGQg+ltry0n0pBSd6yfLaizsIGGS56eaeUhILTqXh8raaDQU0jdXy0IU3BhZpu+v" type="audio/wav" />
      </audio>
      
      {/* Incoming call overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
          {/* Caller Avatar */}
          <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg">
            {incomingCall.from_user.photos?.[0] ? (
              <img 
                src={incomingCall.from_user.photos[0]} 
                alt={incomingCall.from_user.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-white flex items-center justify-center">
                <span className="text-5xl text-pink-500 font-bold">
                  {incomingCall.from_user.name[0]}
                </span>
              </div>
            )}
          </div>
          
          {/* Caller Info */}
          <h2 className="text-3xl font-bold text-white mb-2">
            {incomingCall.from_user.name}
          </h2>
          <p className="text-white text-lg mb-2">
            {incomingCall.call_type === 'video' ? 'Video' : 'Audio'} Call
          </p>
          <p className="text-pink-100 text-sm mb-8 animate-pulse">
            Incoming call...
          </p>
          
          {/* Action Buttons */}
          <div className="flex justify-center space-x-4">
            <Button
              onClick={rejectCall}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full w-16 h-16 p-0 shadow-lg"
            >
              <PhoneOff className="w-8 h-8" />
            </Button>
            
            <Button
              onClick={acceptCall}
              className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 p-0 shadow-lg animate-bounce"
            >
              {incomingCall.call_type === 'video' ? (
                <Video className="w-8 h-8" />
              ) : (
                <Phone className="w-8 h-8" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomingCallHandler;
